import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import {
  approveSubTaskExpense,
  voidSubTaskExpense
} from '../../services/api/implementation/impl';
import { formatRupees } from './budget';

const SpendApprovalModal = ({ items, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [doneIds, setDoneIds] = useState(() => new Set());
  const expenses = (items || [])
    .map((item) => item.expense)
    .filter((row) => row && !doneIds.has(row.id));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['managementSpendApprovals'] });
    queryClient.invalidateQueries({ queryKey: ['priorities'] });
    queryClient.invalidateQueries({ queryKey: ['managementTask'] });
  };

  const markDone = (id) => {
    setDoneIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      const left = (items || []).filter(
        (item) => item.expense?.id && !next.has(item.expense.id)
      );
      if (left.length === 0) {
        setTimeout(onClose, 0);
      }
      return next;
    });
    invalidate();
  };

  const approveMutation = useMutation({
    mutationFn: approveSubTaskExpense,
    onSuccess: (_data, expenseId) => {
      toast.success('Spend approved');
      markDone(expenseId);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to approve spend');
    }
  });

  const voidMutation = useMutation({
    mutationFn: voidSubTaskExpense,
    onSuccess: (_data, expenseId) => {
      toast.success('Spend cancelled');
      markDone(expenseId);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to cancel spend');
    }
  });

  const busy = approveMutation.isPending || voidMutation.isPending;

  const handleApprove = (expense) => {
    if (expense.overBudget) {
      const ok = window.confirm('This line exceeds remaining allocation. Approve as owner?');
      if (!ok) return;
    }
    approveMutation.mutate(expense.id);
  };

  return (
    <Modal name="Approve spend" width={50} onClose={onClose}>
      {expenses.length === 0 ? (
        <p className="text-sm text-gray-500">No draft spend pending.</p>
      ) : (
        <ul className="space-y-3">
          {expenses.map((expense) => (
            <li
              key={expense.id}
              className="border border-gray-200 rounded-lg p-3 bg-gray-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {formatRupees(expense.amount)}
                  </p>
                  <button
                    type="button"
                    className="text-sm text-[#346C75] hover:underline mt-0.5 text-left"
                    onClick={() => {
                      if (expense.task?.id) {
                        navigate(`/builder/management/tasks/${expense.task.id}`);
                      }
                    }}
                  >
                    {expense.task?.title || 'Management Task'}
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    {expense.subTask?.title || 'Subtask'}
                    {expense.user?.name ? ` · Added by ${expense.user.name}` : ''}
                  </p>
                  {expense.note && (
                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap break-words">
                      {expense.note}
                    </p>
                  )}
                  {expense.overBudget ? (
                    <p className="text-xs text-amber-700 mt-1">
                      This line exceeds remaining allocation
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleApprove(expense)}
                    className="px-3 py-1 rounded text-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => voidMutation.mutate(expense.id)}
                    className="px-3 py-1 rounded text-sm text-[#346C75] border border-[#346C75] hover:bg-[#EBF1F2] disabled:opacity-50"
                  >
                    Cancel spend
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
};

export default SpendApprovalModal;

